import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["imageWrapper"]
  static values = {
    productId: Number,
    fieldName: String,
    url: String
  }

  connect() {
    console.log("Product Images Dropzone connected", this.element)
    console.log("Field name:", this.fieldNameValue)
    console.log("URL:", this.urlValue)

    // Initialize drag counter to track nested drag events
    this.dragCounter = 0
    this.isDragging = false

    // Find the dropzone container element
    const dropzoneElement = this.element.querySelector('.dropzone-container')
    if (!dropzoneElement) {
      console.error("Could not find .dropzone-container element")
      return
    }

    // Prevent default browser drag/drop behavior
    dropzoneElement.addEventListener('dragover', this.handleDragOver.bind(this))
    dropzoneElement.addEventListener('drop', this.handleDrop.bind(this))

    // Track drag enter/leave with counter for nested elements
    dropzoneElement.addEventListener('dragenter', this.handleDragEnter.bind(this))
    dropzoneElement.addEventListener('dragleave', this.handleDragLeave.bind(this))

    // Prevent click on image when dragging
    if (this.hasImageWrapperTarget) {
      this.imageWrapperTarget.addEventListener('click', this.handleImageClick.bind(this), true)
    }

    Dropzone.autoDiscover = false

    try {
      const dropzone = new Dropzone(dropzoneElement, {
        url: this.urlValue,
        maxFiles: 1,
        maxFilesize: 10, // MB
        acceptedFiles: "image/*",
        addRemoveLinks: false,
        dictDefaultMessage: "Drop image here or click to upload",
        timeout: 600000, // 10 minutes
        autoQueue: false,
        clickable: true,
        previewsContainer: false // Disable preview container since we show images directly
      })

      console.log("Dropzone initialized successfully", dropzone)

      dropzone.on("addedfile", (file) => {
        console.log("File added:", file)
        // Remove any existing files (since maxFiles is 1)
        if (dropzone.files.length > 1) {
          dropzone.removeFile(dropzone.files[0])
        }

        // Show loading spinner
        this.showLoadingSpinner()

        // Create Direct Upload
        const upload = new DirectUpload(file, this.directUploadUrl)

        upload.create((error, blob) => {
          if (error) {
            console.error("Upload error:", error)
            this.hideLoadingSpinner()
            dropzone.emit("error", file, error)
            alert("Failed to upload image. Please try again.")
          } else {
            console.log("Upload successful, blob:", blob)
            // Submit the form with the blob signed_id
            this.submitImage(blob.signed_id)
          }
        })
      })

      dropzone.on("removedfile", (file) => {
        // Optionally handle image removal
        console.log("File removed:", file)
      })
    } catch (error) {
      console.error("Failed to initialize Dropzone:", error)
    }
  }

  submitImage(signedId) {
    // Create form data
    const formData = new FormData()
    formData.append(`product[${this.fieldNameValue}]`, signedId)

    // Submit via fetch
    fetch(this.urlValue, {
      method: 'PATCH',
      body: formData,
      headers: {
        'X-CSRF-Token': this.csrfToken,
        'Accept': 'application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          console.error("Server error response:", text)
          throw new Error(`HTTP ${response.status}: ${text.substring(0, 200)}`)
        })
      }
      return response.json()
    })
    .then(data => {
      // Successfully updated - reload the page to show new image
      Turbo.visit(window.location.href, { action: "replace" })
    })
    .catch(error => {
      console.error("Upload error:", error)
      this.hideLoadingSpinner()
      alert("Failed to upload image. Please try again.")
    })
  }

  get directUploadUrl() {
    return document.querySelector('meta[name="direct-upload-url"]')?.content || '/rails/active_storage/direct_uploads'
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  handleDragEnter(e) {
    e.preventDefault()
    e.stopPropagation()

    // Only show overlay if dragging files
    if (e.dataTransfer && e.dataTransfer.types.includes('Files')) {
      this.dragCounter++
      if (this.dragCounter === 1) {
        this.isDragging = true
        this.showDragOverlay()
      }
    }
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()

    if (this.isDragging) {
      this.dragCounter--
      if (this.dragCounter === 0) {
        this.isDragging = false
        this.hideDragOverlay()
      }
    }
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()

    // Only if dragging files
    if (e.dataTransfer && e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy'
    }
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()

    this.dragCounter = 0
    this.isDragging = false
    this.hideDragOverlay()
  }

  handleImageClick(e) {
    // Prevent click-to-preview when we just finished dragging
    if (this.isDragging) {
      e.preventDefault()
      e.stopPropagation()
      return false
    }
  }

  showDragOverlay() {
    const overlay = this.element.querySelector('.dropzone-overlay')
    const message = this.element.querySelector('.dropzone-message')

    if (overlay && message) {
      overlay.classList.add('bg-blue-500/30', 'border-blue-500')
      overlay.classList.remove('pointer-events-none')
      overlay.classList.add('pointer-events-auto')
      message.classList.remove('hidden')
      message.classList.add('flex')

      // Dim the underlying image to make overlay more obvious
      if (this.hasImageWrapperTarget) {
        this.imageWrapperTarget.style.opacity = '0.4'
      }
    }
  }

  hideDragOverlay() {
    const overlay = this.element.querySelector('.dropzone-overlay')
    const message = this.element.querySelector('.dropzone-message')

    if (overlay && message) {
      overlay.classList.remove('bg-blue-500/30', 'border-blue-500', 'pointer-events-auto')
      overlay.classList.add('pointer-events-none')
      message.classList.remove('flex')
      message.classList.add('hidden')

      // Restore image opacity
      if (this.hasImageWrapperTarget) {
        this.imageWrapperTarget.style.opacity = '1'
      }
    }
  }

  showLoadingSpinner() {
    const spinner = this.element.querySelector('.loading-spinner')
    if (spinner) {
      spinner.classList.remove('hidden')
    }
  }

  hideLoadingSpinner() {
    const spinner = this.element.querySelector('.loading-spinner')
    if (spinner) {
      spinner.classList.add('hidden')
    }
  }
}
